import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NewStoreSchema,
  StoreBrandFiles,
  StorePrimaryInfo,
} from '../../../schemas/storeGetStartedSchema';
import { saveStore } from '../saveStore';
import uploadFile from '../../fileUpload/uploadFile';
import { FilePurpose } from '../../../libs/interfaces/filePurpose';
import { usePageLoader } from '../../../context/pageLoader';
import useIonicToast from '../../../hooks/useIonicToast';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { saveStoreOfficialDocs } from '../saveStoreOfficialDocs';

type MainFormType = z.infer<typeof NewStoreSchema>;
const StorePrimaryInfoFields = Object.keys(
  StorePrimaryInfo.shape
) as unknown as keyof MainFormType;

export default function useStoreServices() {
  const queryClient = useQueryClient();
  const invalidateProfile = () =>
    queryClient.invalidateQueries({ queryKey: ['user-profile'] });

  const { setBusy } = usePageLoader();
  const { presentIonToast } = useIonicToast();

  const activeStore = useSelector(
    (state: RootState) => state.userStores.activeStore
  );
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );

  const store_id = activeStore?.id || undefined;
  const role_id = userProfile?.role?.id || undefined;

  const methods = useForm<MainFormType>({
    resolver: zodResolver(NewStoreSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      description: '',
      address: '',
      contact_email: '',
      contact_phone: '',
      TAX_IDENTIFICATION_NUMBER: '',
    },
  });

  useEffect(() => {
    if (!activeStore) return;
    // Existing images (logo, banner, official docs) are displayed via direct
    // `<img src={url}>` inside FileDropBox / DocumentStatusField, so we don't
    // need to fetch them into File objects up front. fetch() also fails on
    // our S3 bucket due to missing CORS headers, while `<img>` works fine.
    methods.reset({
      name: activeStore.store_name,
      description: activeStore.description,
      address: activeStore.store_address,
      store_id: activeStore.id,
      logo: activeStore.media.logo?.id || undefined,
      banner_image: activeStore.media.banner?.id || undefined,
      contact_email: activeStore.store_email || undefined,
      contact_phone: activeStore.store_phone || undefined,
      ownerId: userProfile?.user.id,
      TAX_IDENTIFICATION_NUMBER: '',
    });
  }, [userProfile, activeStore, methods]);

  // TRIGGER FOR STORE PRIMARY INFORMATION
  const triggerStoreInfoValidation = () =>
    methods.trigger(StorePrimaryInfoFields);

  /**
   * Mutation for saving general store info
   */
  const storeInfoMutation = useMutation({
    mutationFn: async () => {
      setBusy(true);
      const { store_logo: _, store_banner: __, ...data } = methods.getValues();

      const res = await saveStore({
        authToken: authToken!,
        body: {
          ...data,
          contact_email: data?.contact_email?.trim().toLowerCase(),
        },
      });
      if (!data.store_id) methods.setValue('store_id', res.id);
    },
    retry: false,
  });

  /**
   * Mutation for saving store images (logo & banner)
   */
  const storeImageMutation = useMutation({
    mutationFn: async (data: z.infer<typeof StoreBrandFiles>) => {
      setBusy(true);

      if (data.store_logo) {
        // STORE LOGO UPLOAD
        const logoUpload = await uploadFile({
          file: data.store_logo,
          file_purpose: FilePurpose.LOGO,
        });

        // UPDATE STORE LOGO ID
        methods.setValue('logo', logoUpload.file_id);
      }

      // STORE BANNER UPLOAD
      if (data.store_banner) {
        // Upload store banner
        const bannerUpload = await uploadFile({
          file: data.store_banner,
          file_purpose: FilePurpose.BANNER,
        });

        // UPDATE STORE LOGO ID
        methods.setValue('banner_image', bannerUpload.file_id);
      }
    },
    retry: false,
  });

  // THIS UPLOADS STORE OFFICIAL DOCS
  const storeOfficialDocsMutation = useMutation({
    mutationFn: async () => {
      setBusy(true);
      if (!authToken || !role_id) return;
      if (!store_id) throw new Error('Please create your store first.');

      // Pull only the fields the user touched in this session so preloaded
      // files for already-saved docs don't get re-uploaded.
      const values = methods.getValues();
      const dirty = methods.formState.dirtyFields;
      const pickIfDirty = <K extends keyof typeof values>(key: K) =>
        dirty[key] ? values[key] : undefined;

      const reqBody = {
        ...values,
        CAC_CERT: pickIfDirty('CAC_CERT') as typeof values.CAC_CERT,
        CAC_NUMBER: pickIfDirty('CAC_NUMBER') as typeof values.CAC_NUMBER,
        TIN_CERTIFICATE: pickIfDirty(
          'TIN_CERTIFICATE'
        ) as typeof values.TIN_CERTIFICATE,
        TAX_IDENTIFICATION_NUMBER: pickIfDirty(
          'TAX_IDENTIFICATION_NUMBER'
        ) as typeof values.TAX_IDENTIFICATION_NUMBER,
        PROOF_OF_ADDRESS: pickIfDirty(
          'PROOF_OF_ADDRESS'
        ) as typeof values.PROOF_OF_ADDRESS,
        ADDRESS: pickIfDirty('ADDRESS') as typeof values.ADDRESS,
        CAC_STATUS_REPORT: pickIfDirty(
          'CAC_STATUS_REPORT'
        ) as typeof values.CAC_STATUS_REPORT,
        MEMORANDUM_AND_ARTICLES: pickIfDirty(
          'MEMORANDUM_AND_ARTICLES'
        ) as typeof values.MEMORANDUM_AND_ARTICLES,
      };

      await saveStoreOfficialDocs({
        authToken,
        body: reqBody,
        store_id,
        role_id,
      });

      presentIonToast('Documents successfully uploaded');
      invalidateProfile();

      methods.reset();
    },
    onError: (error: any) => {
      presentIonToast(error.message, 'error');
    },
    onSettled: () => setBusy(false),
  });

  /**
   * Handles saving general store information
   */
  const handleSaveStoreInfo = async () => {
    try {
      setBusy(true);
      const { store_id } = methods.getValues();
      const isValid = await triggerStoreInfoValidation();
      if (!isValid || !authToken) return;

      await storeInfoMutation.mutateAsync();
      invalidateProfile();
      methods.reset();

      presentIonToast(
        `store successfully ${store_id ? 'updated' : 'created'}.`
      );
    } catch (error: any) {
      presentIonToast(error.message, 'error');
      console.error('Failed to save store info:', error);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Handles saving store image only (logo + banner)
   */
  const handleSaveStoreImage = async () => {
    try {
      setBusy(true);
      const { store_logo, logo, store_banner, banner_image } =
        methods.getValues();

      let triggerArr: Array<keyof MainFormType> = [];

      if (!logo) triggerArr.push('store_logo');
      if (!banner_image) triggerArr.push('store_banner');

      const isValid = await methods.trigger(triggerArr);
      if (!isValid || !authToken) return;

      if (!store_logo && !store_banner) return;

      await storeImageMutation.mutateAsync({ store_logo, store_banner });

      // THIS STEP IS FOR UPDATING OR CRATING STORE IF STORE INFO HAS BEEN PROVIDED
      if (store_id) {
        await storeInfoMutation.mutateAsync();
        presentIonToast('Store brand saved', 'success');
      }
      presentIonToast('Store branding saved. Complete your store details.');
      invalidateProfile();

      methods.reset();
    } catch (error: any) {
      presentIonToast(error.message, 'error');
      console.error('Failed to save store images:', error);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save only the docs the user touched in this session. Pair-validate
   * file+number combos (CAC, TIN, address) so a user can't submit just half
   * of a pair, but skip everything they didn't fill — partial submissions
   * are fine.
   */
  const handleSaveStoreOfficialDocs = async () => {
    const values = methods.getValues();
    const dirty = methods.formState.dirtyFields;

    // CAC and proof-of-address still require both the file and its number.
    // TIN is handled separately below: the number alone is enough and the
    // certificate is optional.
    const pairs: Array<{
      file: keyof typeof values;
      meta: keyof typeof values;
      fileLabel: string;
      metaLabel: string;
    }> = [
      {
        file: 'CAC_CERT',
        meta: 'CAC_NUMBER',
        fileLabel: 'CAC certificate',
        metaLabel: 'CAC number',
      },
      {
        file: 'PROOF_OF_ADDRESS',
        meta: 'ADDRESS',
        fileLabel: 'proof of address',
        metaLabel: 'address',
      },
    ];

    methods.clearErrors([
      'CAC_CERT',
      'CAC_NUMBER',
      'TIN_CERTIFICATE',
      'TAX_IDENTIFICATION_NUMBER',
      'PROOF_OF_ADDRESS',
      'ADDRESS',
    ]);

    let hasError = false;
    for (const { file, meta, fileLabel, metaLabel } of pairs) {
      const fileDirty = Boolean(dirty[file]);
      const metaDirty = Boolean(dirty[meta]);
      if (!fileDirty && !metaDirty) continue;

      if (!values[file]) {
        methods.setError(file as any, {
          message: `Please upload ${fileLabel}`,
        });
        hasError = true;
      }
      const metaValue = values[meta];
      if (typeof metaValue !== 'string' || !metaValue.trim()) {
        methods.setError(meta as any, {
          message: `${metaLabel.charAt(0).toUpperCase() + metaLabel.slice(1)} is required`,
        });
        hasError = true;
      }
    }

    // TIN: the number is the source of truth. If the user touched either the
    // TIN number or the certificate, require the number; the certificate is an
    // optional supporting document.
    const tinNumber = values.TAX_IDENTIFICATION_NUMBER;
    const tinTouched =
      Boolean(dirty.TAX_IDENTIFICATION_NUMBER) ||
      Boolean(dirty.TIN_CERTIFICATE);
    if (tinTouched && (typeof tinNumber !== 'string' || !tinNumber.trim())) {
      methods.setError('TAX_IDENTIFICATION_NUMBER' as any, {
        message: 'TIN is required',
      });
      hasError = true;
    }

    if (hasError) return;

    // A submittable item is any staged document file OR the TIN number on its
    // own (certificate optional).
    const docFields: Array<keyof typeof values> = [
      'CAC_CERT',
      'CAC_STATUS_REPORT',
      'TIN_CERTIFICATE',
      'PROOF_OF_ADDRESS',
      'MEMORANDUM_AND_ARTICLES',
    ];
    const hasAnyDirtyDoc =
      docFields.some((f) => Boolean(dirty[f])) ||
      Boolean(dirty.TAX_IDENTIFICATION_NUMBER);
    if (!hasAnyDirtyDoc) {
      presentIonToast('Upload at least one document to submit.', 'error');
      return;
    }

    await storeOfficialDocsMutation.mutateAsync();
  };

  const handleUpdateStoreProfile = async () => {
    try {
      setBusy(true);
      if (!authToken) return;
      const isValid = await triggerStoreInfoValidation();

      if (!isValid) return;
      const isBrandValid = await methods.trigger([
        'store_logo',
        'store_banner',
      ]);

      if (!isBrandValid) return;
      const { store_logo, store_banner } = methods.getValues();

      if (store_logo || store_banner) {
        await storeImageMutation.mutateAsync({ store_logo, store_banner });
      }

      await storeInfoMutation.mutateAsync();
      methods.reset({
        store_logo: undefined,
        store_banner: undefined,
      });
      presentIonToast('store successfully updated');
      invalidateProfile();

      methods.reset();
    } catch (error: any) {
      presentIonToast(error.message);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  return {
    methods,
    handleSaveStoreInfo,
    handleSaveStoreImage,
    triggerStoreInfoValidation,
    handleSaveStoreOfficialDocs,
    handleUpdateStoreProfile,
  };
}
